import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { buttonClasses, type ButtonSize, type ButtonVariant } from './buttonStyles';
import { Spinner } from './Spinner';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Accessible name — required because there is no visible text. */
  label: string;
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: Extract<ButtonSize, 'icon' | 'icon-sm'>;
  loading?: boolean;
}

/** Icon-only button with a required accessible label. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, variant = 'ghost', size = 'icon', loading = false, className, disabled, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={buttonClasses(variant, size, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={16} /> : icon}
    </button>
  );
});

export function iconButtonClass(variant: ButtonVariant = 'ghost', size: Extract<ButtonSize, 'icon' | 'icon-sm'> = 'icon', className?: string) {
  return cn(buttonClasses(variant, size, className));
}
